import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Spade, Trophy, Diamond, Bell, CircleDollarSign, Magnet } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PreviewRibbon, GamblingNotice } from "@/components/cef/cefShared";
import { usePhoneAudio } from "@/components/cef/phoneAudio";

/* Standalone SLOTS widget — one page, sized to sit on the slot machine's 3D
   screen as an in-world widget (guide §7.4). Fully transparent root; the cabinet
   below is the whole page. Same backend + math as the casino floor machine. */

const GOLD = "#d4af37";
const fmt = (n) => `$${(n ?? 0).toLocaleString()}`;
const ROOM_CHIPS = { main: [10, 25, 50, 100], high: [500, 1000, 2500, 5000] };
const SLOT_SYMBOLS = ["seven", "diamond", "bell", "spade", "chip", "horseshoe"];
const SYM_META = {
  seven: { color: GOLD, label: "7" },
  diamond: { color: "#22d3ee", Icon: Diamond },
  bell: { color: "#fbbf24", Icon: Bell },
  spade: { color: "#d4d4d8", Icon: Spade },
  chip: { color: "#34d399", Icon: CircleDollarSign },
  horseshoe: { color: "#fb923c", Icon: Magnet },
};
const Sym = ({ id, size = 38 }) => {
  const m = SYM_META[id] || SYM_META.chip;
  if (m.label) return <span className="font-black" style={{ fontSize: size, color: m.color, textShadow: `0 0 ${size / 2}px ${m.color}88`, fontFamily: "'Rajdhani',sans-serif" }}>{m.label}</span>;
  return <m.Icon style={{ width: size, height: size, color: m.color, filter: `drop-shadow(0 0 ${size / 3}px ${m.color}66)` }} />;
};
const SYM_H = 84;

function useSlotAudio() {
  return useMemo(() => {
    let ctx = null;
    const ac = () => {
      if (!ctx) ctx = new (window.AudioContext || window.webkitAudioContext)();
      if (ctx.state === "suspended") ctx.resume();
      return ctx;
    };
    const tone = (freq, at, dur, type = "square", vol = 0.05) => {
      try {
        const c = ac(), o = c.createOscillator(), g = c.createGain();
        o.type = type; o.frequency.value = freq;
        g.gain.setValueAtTime(vol, at);
        g.gain.exponentialRampToValueAtTime(0.0001, at + dur);
        o.connect(g).connect(c.destination);
        o.start(at); o.stop(at + dur + 0.02);
      } catch { /* no audio device */ }
    };
    const now = () => ac().currentTime;
    return {
      lever() { const t = now(); tone(150, t, 0.07, "square", 0.09); tone(95, t + 0.08, 0.14, "sine", 0.12); },
      ratchet() { const t = now(); for (let i = 0; i < 14; i++) tone(1200 + i * 60, t + i * 0.045, 0.03, "square", 0.022); },
      reelStop() { const t = now(); tone(220, t, 0.05, "triangle", 0.1); tone(110, t + 0.03, 0.1, "sine", 0.12); },
      win() { const t = now(); [523, 659, 784].forEach((f, i) => tone(f, t + i * 0.09, 0.16, "triangle", 0.07)); },
      jackpot() { const t = now(); [523, 659, 784, 1047, 784, 1047, 1319, 1568].forEach((f, i) => tone(f, t + i * 0.11, 0.2, "triangle", 0.08)); [261, 329, 392].forEach((f) => tone(f, t, 0.9, "sawtooth", 0.02)); },
      coins() { const t = now(); for (let i = 0; i < 12; i++) tone(2200 + Math.random() * 1400, t + 0.6 + i * 0.07, 0.05, "square", 0.028); },
    };
  }, []);
}

export default function SlotsCefPage() {
  const { serverId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const demo = !token;
  const room = params.get("room") === "high" ? "high" : "main";

  const [bank, setBank] = useState(demo ? 5000 : null);
  const [slotBet, setSlotBet] = useState(ROOM_CHIPS[room][1]);
  const [slotBusy, setSlotBusy] = useState(false);
  const [reels, setReels] = useState(["seven", "bell", "diamond"]);
  const [reelSpinning, setReelSpinning] = useState([false, false, false]);
  const [reelPos, setReelPos] = useState([0, 2, 1]); // strip positions matching the initial symbols
  const [slotWin, setSlotWin] = useState(null);
  const [payoutCount, setPayoutCount] = useState(0);
  const [jackpotFlash, setJackpotFlash] = useState(false);
  const [leverPulled, setLeverPulled] = useState(false);
  const sfx = usePhoneAudio();
  const slotAudio = useSlotAudio();
  const timers = useRef([]);
  const later = (fn, ms) => timers.current.push(setTimeout(fn, ms));
  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  // scale the fixed-px cabinet to fill the viewport (reel math stays in px)
  const [cabScale, setCabScale] = useState(1);
  useEffect(() => {
    const upd = () => setCabScale(Math.min(window.innerWidth / 520, window.innerHeight / 640));
    upd();
    window.addEventListener("resize", upd);
    return () => window.removeEventListener("resize", upd);
  }, []);

  useEffect(() => {
    document.documentElement.style.background = "transparent";
    document.body.style.background = "transparent";
    return () => { document.documentElement.style.background = ""; document.body.style.background = ""; };
  }, []);

  const load = useCallback(() => {
    if (demo) return;
    api.get(`/nc/servers/${serverId}/casino/state`, { params: { token } })
      .then((r) => setBank(r.data.bank))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [demo, serverId, token]);
  useEffect(load, [load]);

  // payout meter count-up + jackpot strobe
  useEffect(() => {
    if (!slotWin) return;
    const target = slotWin.payout;
    let v = 0;
    const step = Math.max(1, Math.round(target / 24));
    setPayoutCount(0);
    const iv = setInterval(() => { v = Math.min(target, v + step); setPayoutCount(v); if (v >= target) clearInterval(iv); }, 45);
    let t;
    if (slotWin.win_kind === "jackpot") { setJackpotFlash(true); t = setTimeout(() => setJackpotFlash(false), 2800); }
    return () => { clearInterval(iv); if (t) clearTimeout(t); };
  }, [slotWin]);

  const evalDemo = (reelIds) => {
    const PAY3 = { seven: 50, diamond: 25, bell: 15, spade: 10, chip: 8, horseshoe: 5 };
    if (reelIds[0] === reelIds[1] && reelIds[1] === reelIds[2]) return { mult: PAY3[reelIds[0]], kind: reelIds[0] === "seven" ? "jackpot" : "three" };
    if (reelIds[0] === reelIds[1]) return { mult: 2, kind: "pair" };
    return { mult: 0, kind: "lose" };
  };

  const spinSlots = async () => {
    if (slotBusy) return;
    if (slotBet > (bank ?? 0)) { toast.error("Not enough in the bank"); return; }
    sfx.click?.();
    slotAudio.lever(); slotAudio.ratchet();
    setSlotBusy(true);
    setSlotWin(null);
    let r;
    if (demo) {
      const reelIds = Array.from({ length: 3 }, () => SLOT_SYMBOLS[Math.floor(Math.random() * SLOT_SYMBOLS.length)]);
      const { mult, kind } = evalDemo(reelIds);
      r = { reels: reelIds, payout: slotBet * mult, win_kind: kind, bet: slotBet };
      setBank((b) => b - slotBet + r.payout);
    } else {
      try {
        const res = await api.post(`/nc/servers/${serverId}/casino/slots/spin`, { bet: slotBet, room }, { params: { token } });
        r = res.data;
        if (r.bank != null) setBank(r.bank); else setBank((b) => (b ?? 0) - slotBet + r.payout);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
        setSlotBusy(false);
        return;
      }
    }
    // strip[j] = SYMBOLS[j % 6], so the stop for symbol idx must satisfy stop ≡ idx (mod 6).
    // Reset to the low twin of the current symbol (identical visual, transition off),
    // then spin to 54 + idx — the displayed symbol now always matches the server result.
    const stops = r.reels.map((sym) => SLOT_SYMBOLS.indexOf(sym) + 54);
    setReels(r.reels);
    setReelSpinning([false, false, false]);
    setReelPos((prev) => prev.map((p) => p % 6));
    later(() => {
      setReelPos(stops);
      setReelSpinning([true, true, true]);
    }, 60);
    r.reels.forEach((_, i) => later(() => {
      setReelSpinning((s) => s.map((v, j) => (j === i ? false : v)));
      slotAudio.reelStop();
    }, 60 + 1500 + i * 550));
    later(() => {
      setSlotBusy(false);
      if (r.payout > 0) {
        if (r.win_kind === "jackpot") { slotAudio.jackpot(); slotAudio.coins(); } else slotAudio.win();
        setSlotWin(r);
        toast.success(r.win_kind === "jackpot" ? `JACKPOT — ${fmt(r.payout)}` : `Winner — ${fmt(r.payout)}`);
      }
    }, 1500 + 3 * 550 + 150);
  };

  const chips = ROOM_CHIPS[room];

  return (
    <div data-testid="slots-widget-page" className="fixed inset-0 overflow-hidden pointer-events-none flex items-center justify-center" style={{ fontFamily: "'Rajdhani','Segoe UI',sans-serif" }}>
      {demo && <PreviewRibbon what="slots widget" />}
      <GamblingNotice />

      <div className="pointer-events-auto flex flex-col items-center px-4 w-full max-w-[430px]"
        style={{ transform: `scale(${cabScale})`, transformOrigin: "center center" }}>
        {/* balance chip */}
        <p className="mb-3 font-mono text-xs font-bold px-4 py-1.5 rounded-sm flex items-center gap-2" style={{ backgroundColor: "#000000cc", color: GOLD, boxShadow: "0 2px 10px #00000099" }}>
          <Spade className="w-3.5 h-3.5" /><span data-testid="slots-widget-balance">{bank == null ? "…" : fmt(bank)}</span>
        </p>

        {/* cabinet */}
        <div data-testid="slots-widget-cabinet" className="relative w-full rounded-2xl px-5 pb-4 pt-1.5"
          style={{
            background: "linear-gradient(165deg, #3a2c12 0%, #241a0a 45%, #120b04 100%)",
            border: `2px solid ${GOLD}88`,
            boxShadow: "0 18px 60px #000000dd, inset 0 2px 0 #d4af3755",
            animation: jackpotFlash ? "ncSwStrobe 0.55s ease-in-out infinite" : "none",
          }}>
          {/* marquee */}
          <div className="relative -mx-5 rounded-t-2xl overflow-hidden py-2 mb-4 border-b border-[#d4af3744]" style={{ background: "linear-gradient(180deg, #171006, #0b0703)" }}>
            <div className="absolute inset-x-3 top-0.5 flex justify-between" aria-hidden>
              {Array.from({ length: 10 }, (_, i) => <span key={i} className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: GOLD, animation: `ncSwBulb 1.6s ease-in-out ${i * 0.15}s infinite` }} />)}
            </div>
            <div className="absolute inset-x-3 bottom-0.5 flex justify-between" aria-hidden>
              {Array.from({ length: 10 }, (_, i) => <span key={i} className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: GOLD, animation: `ncSwBulb 1.6s ease-in-out ${(i + 5) * 0.15}s infinite` }} />)}
            </div>
            <p className="relative text-center font-black uppercase tracking-[0.3em] text-sm" style={{ color: GOLD, textShadow: `0 0 16px ${GOLD}aa` }}>The Gilded Spade</p>
            <p className="relative text-center font-mono text-[7px] uppercase tracking-[0.4em] text-zinc-500">Liberty Belle · {room === "high" ? "high roller" : "main floor"}</p>
          </div>

          {/* reel window */}
          <div className="relative">
            <div className="relative flex gap-2.5 p-3.5 rounded-lg" style={{ backgroundColor: "#050505", boxShadow: "inset 0 0 30px #000" }}>
              {reels.map((sym, i) => (
                <div key={i} data-testid={`widget-reel-${i}`} className="relative flex-1 overflow-hidden rounded border border-[#d4af3733]" style={{ height: SYM_H }}>
                  <div className="absolute left-0 w-full flex flex-col items-center"
                    style={{
                      transform: `translateY(${-reelPos[i] * SYM_H}px)`,
                      transition: reelSpinning[i] ? `transform ${1.5 + i * 0.55}s cubic-bezier(0.25, 0.7, 0.2, 1)` : "none",
                      filter: reelSpinning[i] ? "blur(2.5px)" : "none",
                    }}>
                    {Array.from({ length: 60 }, (_, j) => SLOT_SYMBOLS[j % SLOT_SYMBOLS.length]).map((id, j) => (
                      <div key={j} className="flex items-center justify-center" style={{ height: SYM_H }}><Sym id={id} size={40} /></div>
                    ))}
                  </div>
                  <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(#00000088, transparent 30%, transparent 70%, #00000088)" }} />
                </div>
              ))}
              <div className="absolute inset-0 rounded-lg pointer-events-none" style={{ background: "linear-gradient(115deg, #ffffff14 0%, transparent 28%, transparent 72%, #ffffff08 100%)" }} />
            </div>
            {/* payline */}
            <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 pointer-events-none flex items-center" aria-hidden>
              <span style={{ borderTop: "7px solid transparent", borderBottom: "7px solid transparent", borderLeft: `9px solid ${GOLD}` }} />
              <span className="flex-1 border-t" style={{ borderColor: `${GOLD}55`, boxShadow: `0 0 8px ${GOLD}66`, animation: slotWin && !slotBusy ? "ncSwPayline 0.7s ease-in-out infinite" : "none" }} />
              <span style={{ borderTop: "7px solid transparent", borderBottom: "7px solid transparent", borderRight: `9px solid ${GOLD}` }} />
            </div>
            {/* win banner */}
            {slotWin && !slotBusy && (
              <div data-testid="widget-slot-result" className="absolute inset-x-3 top-1/2 -translate-y-1/2 text-center py-3 border backdrop-blur-sm z-10"
                style={{ backgroundColor: "#000000dd", borderColor: `${GOLD}88`, animation: "ncSwFadeUp 0.3s ease both" }}>
                <Trophy className="w-5 h-5 mx-auto mb-0.5" style={{ color: GOLD }} />
                <p className="font-black text-xl tracking-[0.1em]" style={{ color: GOLD, textShadow: `0 0 20px ${GOLD}aa` }}>{slotWin.win_kind === "jackpot" ? "JACKPOT" : "WINNER"}</p>
                <p data-testid="widget-slot-payout" className="font-mono text-base font-bold text-emerald-400">+{fmt(payoutCount)}</p>
              </div>
            )}
          </div>

          {/* lever */}
          <button data-testid="slots-widget-lever" disabled={slotBusy || slotBet > (bank ?? 0)}
            onClick={() => { setLeverPulled(true); spinSlots(); later(() => setLeverPulled(false), 600); }}
            className="absolute -right-3 top-[34%] flex flex-col items-center gap-1 disabled:opacity-40 group" aria-label="Pull the lever">
            <span className="w-2 rounded-full transition-all duration-300" style={{ height: leverPulled ? 34 : 58, background: "linear-gradient(180deg,#6b7280,#374151)", boxShadow: "inset 0 0 4px #000" }} />
            <span className={`w-6 h-6 rounded-full border-2 transition-transform duration-300 ${leverPulled ? "-translate-y-8" : ""}`} style={{ background: "radial-gradient(circle at 35% 30%, #ff8a3c, #b45309)", borderColor: "#7c2d12", boxShadow: "0 0 14px #ff6b1a88" }} />
          </button>

          {/* bets + spin */}
          <div className="flex items-center gap-2 mt-4">
            <div className="flex gap-1">
              {chips.map((c) => (
                <button key={c} data-testid={`widget-bet-${c}`} onClick={() => { setSlotBet(c); sfx.keyTick(); }}
                  className={`px-2.5 py-1.5 font-mono text-[9px] border transition-colors ${slotBet === c ? "text-black" : "text-zinc-400 border-white/15"}`}
                  style={slotBet === c ? { backgroundColor: GOLD, borderColor: GOLD } : {}}>{c}</button>
              ))}
            </div>
            <button data-testid="slots-widget-spin" disabled={slotBusy || slotBet > (bank ?? 0)} onClick={spinSlots}
              className="flex-1 py-2.5 font-mono text-[10px] uppercase tracking-[0.3em] font-bold text-black disabled:opacity-40"
              style={{ backgroundColor: GOLD, boxShadow: `0 0 20px ${GOLD}44` }}>
              {slotBusy ? "Rolling…" : `Spin ${fmt(slotBet)}`}
            </button>
          </div>

          {/* coin tray */}
          <div className="mt-3 rounded-b-xl border-t border-[#d4af3722] pt-1.5 pb-1 relative overflow-hidden" style={{ background: "linear-gradient(180deg,#0c0804,#050301)", minHeight: 24 }}>
            {jackpotFlash && Array.from({ length: 12 }, (_, i) => (
              <span key={i} className="absolute w-2 h-2 rounded-full" aria-hidden
                style={{ left: `${6 + i * 7.5}%`, top: 0, background: "radial-gradient(circle at 35% 30%, #ffe08a, #b8860b)", animation: `ncSwCoin ${0.8 + (i % 5) * 0.13}s ease-in ${i * 0.07}s infinite` }} />
            ))}
            <p className="text-center font-mono text-[7px] uppercase tracking-[0.4em] text-zinc-600">{jackpotFlash ? "Jackpot — payout raining" : "Liberty Belle"}</p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes ncSwFadeUp { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translate(0, 0); } }
        @keyframes ncSwBulb { 0%, 100% { opacity: 0.2; } 50% { opacity: 1; } }
        @keyframes ncSwStrobe { 0%, 100% { box-shadow: 0 18px 60px #000000dd, inset 0 2px 0 #d4af3755; } 50% { box-shadow: 0 0 110px #d4af37bb, inset 0 2px 0 #d4af37; } }
        @keyframes ncSwPayline { 0%, 100% { opacity: 0.35; } 50% { opacity: 1; } }
        @keyframes ncSwCoin { 0% { transform: translateY(-8px) rotate(0deg); opacity: 0; } 15% { opacity: 1; } 100% { transform: translateY(28px) rotate(240deg); opacity: 0; } }
      `}</style>
    </div>
  );
}
